import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { subscribeLiveClasses } from '../../../utils/liveClassHelpers';

const LiveLessonView = ({ lesson, orgId, courseId }) => {
  const navigate = useNavigate();
  const [relevantClass, setRelevantClass] = useState(null);
  const [checkedFirestore, setCheckedFirestore] = useState(false);

  useEffect(() => {
    if (!orgId || !courseId) {
      setCheckedFirestore(true);
      return;
    }
    const unsubscribe = subscribeLiveClasses(orgId, (all) => {
      const forThisCourse = all?.filter((c) => c?.courseId === courseId);

      // Prefer an explicit link from the lesson, if one was set.
      const linked = lesson?.liveClassId
        ? forThisCourse?.find((c) => c?.id === lesson?.liveClassId)
        : null;
      if (linked) {
        setRelevantClass(linked);
        setCheckedFirestore(true);
        return;
      }

      // Otherwise: prefer whichever is live right now, else the soonest
      // upcoming scheduled one.
      const live = forThisCourse?.find((c) => c?.status === 'live');
      if (live) {
        setRelevantClass(live);
        setCheckedFirestore(true);
        return;
      }
      const upcoming = forThisCourse
        ?.filter((c) => c?.status === 'scheduled')
        ?.sort((a, b) => (a?.scheduledAtMs || 0) - (b?.scheduledAtMs || 0))?.[0];
      setRelevantClass(upcoming || null);
      setCheckedFirestore(true);
    });
    return unsubscribe;
  }, [orgId, courseId, lesson?.liveClassId]);

  const formatSchedule = (ms) => {
    if (!ms) return null;
    return new Date(ms).toLocaleString(undefined, {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  };

  const handleJoin = () => {
    navigate(`/live-classroom-viewer?orgId=${orgId}&classId=${relevantClass?.id}`);
  };

  // Still resolving Firestore — avoid a flash of the legacy fallback UI.
  if (!checkedFirestore) {
    return <div className="bg-card rounded-lg p-12 text-center text-muted-foreground text-sm">Loading session details...</div>;
  }

  // A real live class exists for this course — this is the primary path
  // going forward.
  if (relevantClass) {
    return (
      <div className="bg-card rounded-lg p-12 text-center">
        <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${relevantClass?.status === 'live' ? 'bg-destructive/10' : 'bg-muted'}`}>
          <Icon name="Video" size={24} className={relevantClass?.status === 'live' ? 'text-destructive' : 'text-muted-foreground'} />
        </div>

        {relevantClass?.status === 'live' && (
          <span className="inline-flex items-center gap-1.5 bg-destructive text-white text-xs font-bold px-2 py-1 rounded-full mb-3">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> LIVE NOW
          </span>
        )}

        <h3 className="text-lg font-semibold text-foreground mb-2">{relevantClass?.title || lesson?.title}</h3>

        {relevantClass?.status === 'scheduled' && relevantClass?.scheduledAtMs && (
          <p className="text-sm text-muted-foreground mb-6 flex items-center justify-center gap-1.5">
            <Icon name="Calendar" size={14} /> {formatSchedule(relevantClass?.scheduledAtMs)}
          </p>
        )}

        {relevantClass?.status === 'live' && (
          <div className="mb-6">
            <Button variant="default" iconName="Video" iconPosition="left" onClick={handleJoin}>
              Join Live Classroom
            </Button>
          </div>
        )}

        {relevantClass?.status === 'scheduled' && (
          <p className="text-sm text-muted-foreground">You'll be able to join once your instructor starts the session.</p>
        )}

        {relevantClass?.status === 'ended' && (
          <p className="text-sm text-muted-foreground">This session has ended.</p>
        )}
      </div>
    );
  }

  // Fallback for legacy lessons that only have the old bare meeting-link
  // fields, with no matching live class in Firestore.
  return (
    <div className="bg-card rounded-lg p-12 text-center">
      <div className="w-14 h-14 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
        <Icon name="Video" size={24} className="text-destructive" />
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-2">Live Session</h3>

      <div className="flex items-center justify-center gap-6 text-sm text-muted-foreground mb-6">
        {lesson?.sessionDate && (
          <span className="flex items-center gap-1.5">
            <Icon name="Calendar" size={14} /> {lesson?.sessionDate}
          </span>
        )}
        {lesson?.sessionTime && (
          <span className="flex items-center gap-1.5">
            <Icon name="Clock" size={14} /> {lesson?.sessionTime}
          </span>
        )}
      </div>

      {lesson?.meetingLink ? (
        <a href={lesson?.meetingLink} target="_blank" rel="noopener noreferrer">
          <Button variant="default" iconName="Video" iconPosition="left">
            Join Session
          </Button>
        </a>
      ) : (
        <p className="text-sm text-muted-foreground">The meeting link hasn't been added yet.</p>
      )}
    </div>
  );
};

export default LiveLessonView;